import classNames from 'classnames';
import { nanoid } from 'nanoid';
import Icon from '~/components2/Icon';
import { ClearFilters, Condition, FilterOperator, RemoveFilter, type TableFilterPropsBase } from '../common';
import { Dropdown } from '../Legacy/LegacyDropdown';
import store from '../store';
import FilterGroup from './FilterGroup';
import FilterRule from './FilterRule';
import Rule from './Rule';

export interface GroupProps<T> extends TableFilterPropsBase<T> {
  group: FilterGroup;
}

export default function Group<T>({ table, columns, parentId, onQuery, group }: GroupProps<T>) {
  return (
    <div className={classNames('relative flex min-w-[48rem] items-start justify-between space-x-2')}>
      <Condition table={table} columns={columns} parentId={parentId} filter={group} />

      <div
        className={classNames('flex grow flex-col space-y-2 rounded bg-[#f4f5f580] bg-opacity-50 p-2 mt-1', {
          'border border-primary-light': !!parentId,
          'bg-transparent': !parentId,
        })}
      >
        <div
          className={classNames('flex grow items-center', {
            'justify-start space-x-4': !parentId,
            'justify-between': !!parentId,
          })}
        >
          <Dropdown
            side="left"
            align="start"
            triggerElement={
              <span className="flex cursor-pointer items-center gap-1 rounded p-2 text-sm hover:bg-gray-100 hover:!opacity-100">
                <Icon icon="plus" /> Add
              </span>
            }
            items={[
              {
                type: 'simple',
                label: 'Filter Rule',
                onClick: () => {
                  store.addFilter(
                    new FilterRule({
                      id: nanoid(),
                      columnId: columns[0].id,
                      operator: FilterOperator.Includes,
                    }),
                    group.id,
                  );
                },
              },
              {
                type: 'simple',
                label: 'Filter Group',
                onClick: () => {
                  store.addFilter(new FilterGroup({ id: nanoid(), condition: 'and' }), group.id);
                },
              },
            ]}
          />

          <RemoveFilter table={table} columns={columns} parentId={parentId} filterId={group.id} />

          <ClearFilters table={table} parentId={parentId} columns={columns} onQuery={onQuery} />
        </div>

        {group.children.map((child) => {
          if (child.type === 'group') {
            return (
              <Group key={`group-${child.id}`} table={table} columns={columns} group={child} parentId={group.id} />
            );
          } else {
            return <Rule key={`rule-${child.id}`} table={table} columns={columns} rule={child} parentId={group.id} />;
          }
        })}
      </div>
    </div>
  );
}
